/**
 * Agent list and detail calls for chat.
 * Payload/response shapes aligned with ann-web-app `src/services/agents.js`; transport uses injected `apiClient`
 * (monorepo `createApiClient` — responses are already unwrapped to JSON body).
 *
 * @param {import('axios').AxiosInstance} apiClient
 */

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {object} [params]
 * @returns {Promise<{ data: { agents: object[], requestId: *, msgCode: *, meta: * } }>}
 */
export const getAgentList = async (apiClient, params = {}) => {
  const body = {
    page: params.page || 1,
    pageSize: params.pageSize || params.page_size || 200,
    orderBy: params.orderBy || params.order_by || 'created_at',
    orderDirection: params.orderDirection || params.order_direction || 'DESC',
  };

  // Only send `filters` when there's something to filter by (matches the
  // agent-list contract used by apps/agents; an empty object is omitted).
  const filters = params.filters || {};
  if (Object.keys(filters).length > 0) {
    body.filters = filters;
  }

  // Search: the backend expects an object `{ query, fields }`, NOT a raw string.
  // Accept a plain string (wrapped into the default name search) or a
  // pre-formed `{ query, fields }` object from the caller.
  if (params.search) {
    if (typeof params.search === 'string') {
      const query = params.search.trim();
      if (query) {
        body.search = { query, fields: ['name'] };
      }
    } else {
      body.search = params.search;
    }
  }

  const data = await apiClient.post('/v1/workspace/agent/list', body);

  return {
    data: {
      agents: data?.data || [],
      requestId: data?.requestId || null,
      msgCode: data?.msgCode || null,
      meta: data?.meta || null,
    },
  };
};

/**
 * @param {import('axios').AxiosInstance} apiClient
 * @param {string} agentUuid
 * @returns {Promise<{ data: object }>}
 */
export const getAgentDetails = async (apiClient, agentUuid) => {
  const data = await apiClient.get(`/v1/workspace/agent/${encodeURIComponent(agentUuid)}`);

  const agentData = data?.body || data;

  const conversationStarters =
    agentData?.metadata?.conversation_starters ||
    agentData?.conversation_starters ||
    [];

  return {
    data: {
      ...agentData,
      conversation_starters: conversationStarters,
      id: agentData?.uuid || agentData?.id,
      uuid: agentData?.uuid,
    },
  };
};
